import React from "react";
import ReactDOM from "react-dom";
import "./i18n";
import { RouterProvider } from "react-router-dom";
import router from "./router/MainRouter";
import * as Sentry from "@sentry/react";
import config from "./config";

// Sentry сам режет РФ-юзеров с сент 2024 (OFAC compliance) — события не
// уходят и в console будут ошибки fetch. Гейтим для .ru hostname.
const sentryAllowedHost =
  typeof window !== "undefined" && window.location
    ? !window.location.hostname.endsWith(".ru")
    : true;

// Инициализация Sentry
// 04.10: ?diag=nosentry — диагностика падения iOS Safari (см. public/index.html)
const sentryDiagOff =
  typeof window !== "undefined" &&
  window.__diagOff &&
  window.__diagOff("nosentry");
if (config.sentryDsn && sentryAllowedHost && !sentryDiagOff) {
  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment,
    
    // Интеграции для мониторинга
    integrations: [
      // Трейсинг навигации
      Sentry.browserTracingIntegration({
        // Для react-router v6
        enableLongTask: true,
      }),
      // Session Replay для записи сессий с ошибками
      Sentry.replayIntegration({
        maskAllText: false, // Маскировать текст (для приватности)
        blockAllMedia: false, // Блокировать медиа
      }),
      // Отлов ошибок из браузерного API
      Sentry.browserApiErrorsIntegration(),
      // Отлов HTTP ошибок
      Sentry.httpClientIntegration(),
    ],

    // Performance Monitoring
    tracesSampleRate: config.sentryTracesSampleRate, // 0.1 = 10% запросов

    // Session Replay
    replaysSessionSampleRate: config.sentryReplaySessionSampleRate, // 10% обычных сессий
    replaysOnErrorSampleRate: config.sentryReplayErrorSampleRate, // 100% сессий с ошибками

    // Дополнительный контекст для ошибок
    beforeSend(event, hint) {
      // Можно добавить кастомную логику перед отправкой
      // Например, фильтровать ошибки или добавлять контекст
      
      // Добавляем информацию о пользователе из localStorage
      try {
        const userData = localStorage.getItem("currentUser");
        if (userData) {
          const user = JSON.parse(userData);
          event.user = {
            id: user.id,
            email: user.email,
            username: user.firstName,
          };
        }
      } catch (e) {
        // Игнорируем ошибки парсинга
      }

      return event;
    },

    // Игнорируем только незначительные ошибки
    ignoreErrors: [
      // Игнорируем ошибки сторонних скриптов
      /^Non-Error promise rejection captured/,
      // Игнорируем ошибки расширений браузера
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
    ],
    
    // Фильтруем события перед отправкой
    beforeSendTransaction(event) {
      // Не отправляем транзакции для статических ресурсов
      if (event.transaction && event.transaction.match(/\.(js|css|png|jpg|svg)$/)) {
        return null;
      }
      return event;
    },

    // Настройка для продакшена
    enabled: config.sentryEnvironment !== "development" || process.env.REACT_APP_SENTRY_DEBUG === "true",
  });

  console.log("✅ Sentry инициализирован:", {
    environment: config.sentryEnvironment,
    dsn: config.sentryDsn.substring(0, 30) + "...",
  });
} else {
  console.warn("⚠️ Sentry DSN не установлен. Добавьте REACT_APP_SENTRY_DSN в .env файл");
}

ReactDOM.render(
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "20px",
        textAlign: "center",
      }}>
        <h1>Что-то пошло не так</h1>
        <p>Мы уже получили уведомление об ошибке и работаем над ее исправлением.</p>
        <details style={{ marginTop: "20px", textAlign: "left", maxWidth: "600px" }}>
          <summary style={{ cursor: "pointer", color: "#666" }}>Детали ошибки</summary>
          <pre style={{
            marginTop: "10px",
            padding: "10px",
            background: "#f5f5f5",
            borderRadius: "4px",
            overflow: "auto",
          }}>
            {error?.toString()}
          </pre>
        </details>
        <button
          onClick={resetError}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            background: "#667eea",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Попробовать снова
        </button>
      </div>
    )}
    onError={(error, errorInfo) => {
      console.error("ErrorBoundary caught:", error, errorInfo);
    }}
  >
    <RouterProvider router={router} />
  </Sentry.ErrorBoundary>,
  document.getElementById("root")
);
